/**
 * Field layer module.
 *
 * Renders farm field boundaries as filled polygons with hover and click.
 * Uses a Mapbox fill + line source/layer pair.
 */

import type { Map } from 'mapbox-gl';
import type { MapField } from '../types/mapTypes';
import { fieldToFeature, featureCollection, pointFeature } from '../utils/geojson';

const SOURCE_ID = 'fields-source';
const FILL_LAYER_ID = 'fields-fill';
const LINE_LAYER_ID = 'fields-line';
const LABEL_LAYER_ID = 'fields-label';
const CENTER_LAYER_ID = 'fields-center';

export const FIELD_COLORS = {
  fill: '#3B82F6',
  fillHover: '#2563EB',
  line: '#1D4ED8',
  selected: '#F59E0B',
};

export function addFieldLayers(map: Map): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: featureCollection([]),
  });

  // Fill layer
  map.addLayer({
    id: FILL_LAYER_ID,
    type: 'fill',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'fill-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        FIELD_COLORS.fillHover,
        ['boolean', ['feature-state', 'selected'], false],
        FIELD_COLORS.selected,
        FIELD_COLORS.fill,
      ],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        0.4,
        0.25,
      ],
    },
    filter: ['==', ['get', '_type'], 'field'],
  });

  // Always render a visible anchor for a mapped field. This also keeps fields
  // discoverable when a legacy row has a center but an incomplete boundary.
  map.addLayer({
    id: CENTER_LAYER_ID,
    type: 'circle',
    source: SOURCE_ID,
    paint: {
      'circle-color': '#2563EB',
      'circle-radius': 6,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
    },
    filter: ['==', ['get', '_type'], 'field-center'],
  });

  // Line layer (boundary outline)
  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'line-color': FIELD_COLORS.line,
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        3,
        2,
      ],
      'line-opacity': 1,
    },
    filter: ['==', ['get', '_type'], 'field'],
  });

  // Label layer
  map.addLayer({
    id: LABEL_LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'text-field': ['coalesce', ['get', 'name'], ['get', 'plotCode'], ''],
      'text-size': 11,
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      'text-allow-overlap': false,
      'text-ignore-placement': false,
    },
    paint: {
      'text-color': '#1E40AF',
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 1.5,
    },
    filter: ['==', ['get', '_type'], 'field'],
  });
}

export function updateFieldData(map: Map, fields: MapField[]): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  const features = fields.flatMap((field) => {
    const result = field.boundary.length >= 3 ? [fieldToFeature(field)] : [];
    if (field.center) {
      result.push(pointFeature(`field-center-${field.id}`, field.center, {
        _type: 'field-center',
        id: field.id,
        name: field.name,
        plotCode: field.plotCode ?? '',
      }));
    }
    return result;
  });
  const fc = featureCollection(features);
  (source as any).setData(fc);
}

export function removeFieldLayers(map: Map): void {
  if (map.getLayer(CENTER_LAYER_ID)) map.removeLayer(CENTER_LAYER_ID);
  if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
  if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export function setFieldSelected(map: Map, fieldId: number | null): void {
  if (!map.getSource(SOURCE_ID)) return;
  // Reset all
  map.querySourceFeatures(SOURCE_ID).forEach((f) => {
    if (f.id != null) {
      map.setFeatureState(
        { source: SOURCE_ID, id: f.id as string | number },
        { selected: false, hover: false },
      );
    }
  });
  // Set selected
  if (fieldId != null) {
    map.setFeatureState(
      { source: SOURCE_ID, id: fieldId },
      { selected: true },
    );
  }
}

export {
  SOURCE_ID as fieldSourceId,
  FILL_LAYER_ID as fieldFillLayerId,
  CENTER_LAYER_ID as fieldCenterLayerId,
};
